
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ComoFunciona() {
  const [activeStep, setActiveStep] = useState(0);
  const [activeFaq, setActiveFaq] = useState(-1);
  
  useEffect(() => {
    // Set page title and meta description
    document.title = 'Como Funciona Nossa Metodologia | 7deOuros';
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Como Funciona",
      "description": "4 passos para transformar seu time comercial em uma máquina de vendas previsível",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://setedeouros.com.br'}/como-funciona`,
      "mainEntity": {
        "@type": "HowTo",
        "name": "Sistema de Aumento de Vendas",
        "description": "Metodologia em 4 passos para transformar equipes comerciais",
        "step": [
          {
            "@type": "HowToStep",
            "name": "Diagnóstico",
            "text": "Análise completa do seu funil de vendas atual"
          },
          {
            "@type": "HowToStep", 
            "name": "Implementação",
            "text": "Colocamos tudo em funcionamento com sua equipe"
          },
          {
            "@type": "HowToStep",
            "name": "Operação", 
            "text": "Acompanhamento e otimização contínua"
          },
          {
            "@type": "HowToStep",
            "name": "Escala",
            "text": "Replicamos o sucesso para toda equipe"
          }
        ]
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  
  const steps = [
    {
      number: 1,
      title: 'Diagnóstico',
      prazo: '72 horas',
      description: 'Análise completa do seu funil de vendas atual',
      entregaveis: [
        '3 métricas decisivas identificadas',
        'Mapeamento de gargalos críticos',
        'Plano estruturado de implementação',
        'Benchmarks do seu setor'
      ]
    },
    {
      number: 2,
      title: 'Implementação',
      prazo: '15 dias',
      description: 'Colocamos tudo em funcionamento com sua equipe',
      entregaveis: [
        'Rotina comercial estruturada',
        'Cadência de reuniões otimizada',
        'Playbook personalizado',
        'Site com agente IA integrado',
        'Dashboards de acompanhamento'
      ]
    },
    {
      number: 3,
      title: 'Operação',
      prazo: 'Diária',
      description: 'Acompanhamento e otimização contínua',
      entregaveis: [
        'Reuniões de acompanhamento',
        'Respostas rápidas a objeções',
        'Aceleração de fechamentos',
        'Ajustes baseados em dados'
      ]
    },
    {
      number: 4,
      title: 'Escala',
      prazo: 'Expansão',
      description: 'Replicamos o sucesso para toda equipe',
      entregaveis: [
        'Metodologia validada',
        'Treinamento da equipe completa',
        'Processos escaláveis',
        'Sistema de crescimento sustentável'
      ]
    }
  ];

  const metricas = [
    {
      title: 'Pipeline Velocity',
      description: 'Velocidade de movimentação dos leads no funil'
    },
    {
      title: 'Conversion Rate',
      description: 'Taxa de conversão entre etapas do processo'
    },
    {
      title: 'Deal Size Growth',
      description: 'Crescimento do ticket médio por vendedor'
    }
  ];
  
  const faqs = [
    {
      question: 'Como funciona o piloto?',
      answer: 'Implementamos nossa metodologia em uma equipe ou vendedor primeiro. Se não houver resultado mensurável em 60 dias, você não paga o valor completo. Após validar o sucesso, expandimos para toda equipe.'
    },
    {
      question: 'Quais integrações são possíveis?',
      answer: 'Integramos com WhatsApp Business, sistemas de agenda (Calendly, Acuity), CRMs populares, dashboards personalizados e principais ferramentas de automação de marketing.'
    },
    {
      question: 'Como são definidos os papéis e responsabilidades?',
      answer: 'Definimos claramente: nossa equipe cuida da estratégia, implementação e otimização; sua equipe executa o dia-a-dia com nosso suporte; e fazemos reuniões semanais de alinhamento.'
    },
    {
      question: 'Quanto tempo para ver os primeiros resultados?',
      answer: 'Primeiros indicadores aparecem em 2-3 semanas após implementação. Resultados mensuráveis sólidos em 60-90 dias. Nossa metodologia é focada em wins rápidos e crescimento sustentável.'
    },
    {
      question: 'O que acontece se não der resultado?',
      answer: 'Se após 60 dias não houver melhoria mensurável nas 3 métricas acordadas, você paga apenas os custos de implementação. É nossa garantia de que acreditamos na metodologia.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
            Como Funciona Nossa Metodologia
          </h1>
          <p className="text-xl lg:text-2xl text-red-100 mb-8 max-w-3xl mx-auto">
            4 passos para transformar seu time comercial em uma máquina de vendas previsível
          </p>
        </div>
      </section>
      
      {/* Steps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Nossa Metodologia em Detalhes
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-12">
            {steps.map((step, index) => (
              <button
                key={index}
                onClick={() => setActiveStep(index)}
                className={`p-4 rounded-lg text-left transition-all ${
                  activeStep === index
                    ? 'bg-red-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center mb-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 ${
                    activeStep === index ? 'bg-white text-red-600' : 'bg-red-600 text-white'
                  }`}>
                    {step.number}
                  </div>
                  <span className="font-semibold">{step.title}</span>
                </div>
                <div className={`text-sm ${activeStep === index ? 'text-red-100' : 'text-gray-500'}`}>
                  {step.prazo}
                </div>
              </button>
            ))}
          </div>
          
          <div className="bg-gray-50 rounded-xl p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {steps[activeStep].title}
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  {steps[activeStep].description}
                </p>
                <div className="flex items-center text-red-600 font-semibold">
                  <i className="ri-time-line mr-2"></i>
                  Prazo: {steps[activeStep].prazo}
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  O que você recebe:
                </h4>
                <ul className="space-y-3">
                  {steps[activeStep].entregaveis.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-1">
                        <i className="ri-check-line text-red-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Metrics Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Medimos Semanalmente
            </h2>
            <p className="text-xl text-gray-600">
              3 métricas decisivas que determinam seu sucesso
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {metricas.map((metrica, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-line-chart-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">
                  {metrica.title}
                </h3>
                <p className="text-gray-600">
                  {metrica.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Perguntas Frequentes
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
                  onClick={() => setActiveFaq(activeFaq === index ? -1 : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-${activeFaq === index ? 'subtract' : 'add'}-line text-red-600`}></i>
                  </div>
                </button>
                
                {activeFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Integration Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Integrações Disponíveis
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-whatsapp-line text-2xl text-green-600"></i>
              </div>
              <h3 className="font-semibold mb-2">WhatsApp Business</h3>
              <p className="text-sm text-gray-600">Automação e acompanhamento</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-semibold mb-2">Sistemas de Agenda</h3>
              <p className="text-sm text-gray-600">Calendly, Acuity e outros</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-dashboard-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="font-semibold mb-2">Dashboards</h3>
              <p className="text-sm text-gray-600">Métricas em tempo real</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-robot-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="font-semibold mb-2">Agente IA</h3>
              <p className="text-sm text-gray-600">Automação inteligente</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
            Pronto para Começar?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Inicie com nossa análise gratuita do seu funil de vendas
          </p>
          <div className="flex justify-center">
            <a
              href="https://wa.me/5544988047699?text=Ol%C3%A1%21%20Quero%20fazer%20uma%20an%C3%A1lise%20gratuita%20do%20meu%20funil%20de%20vendas.%20Quando%20podemos%20conversar%3F"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-white text-red-600 hover:bg-gray-100 shadow-lg hover:shadow-xl px-8 py-4 text-lg rounded-xl"
            >
              Análise do Funil
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
